import { createSlice } from "@reduxjs/toolkit";
import axios from "utils/axios";
import { dispatch } from "../index";
import { displayToast } from "./snackbar";
import { SnackbarType } from "utils/constants/SnackbarType";

export const initialState = {
  containerCatalog: {
    count: 0,
    rows: [],
  },
  tableView: "grid", // "grid" for table, "list" for cards
  containerConfiguration: {
    count: 0,
    rows: [],
  }
};

const slice = createSlice({
  name: "container",
  initialState,
  reducers: {
    resetContainerCatalog(state) {
      state.containerCatalog = initialState.containerCatalog;
    },
    getContainerCatalogSuccess(state, action) {
      state.containerCatalog = action.payload;
    },
    setTableView(state, action) {
      state.tableView = action.payload;
    },
    resetContainerConfiguration(state) {
      state.containerConfiguration = initialState.containerConfiguration;
    },
    getContainerConfigurationSuccess(state, action) {
      state.containerConfiguration = action.payload;
    },
  },
});

export default slice.reducer;

export const containerActions = slice.actions;

// Catalog, Global level

export function getContainerCatalog({
  pageNumber,
  pageSize,
  sortField,
  sortDirection,
  searchFilter,
}) {
  return async () => {
    try {
      let params = {
        page: pageNumber,
        pageSize: pageSize,
      };
      if (sortField) {
        params.sort = sortField;
      }
      if (sortDirection) {
        params.sortDirection = sortDirection.toUpperCase(); //Available values : ASC, DESC
      }

      params = { ...params, ...searchFilter };

      const response = await axios.get(
        `${process.env.REACT_APP_TOOLING_API}/tooling/container`,
        { params },
      );
      dispatch(slice.actions.getContainerCatalogSuccess(response.data));
    } catch (error) {
      dispatch(
        displayToast("Failed to retrieve the data for Container Catalog.", SnackbarType.Negative)
      );
      dispatch(slice.actions.resetContainerCatalog());
    }
  };
}

export async function getContainerCatalogId(tempId) {
  return axios.get(
    `${process.env.REACT_APP_TOOLING_API}/tooling/container/${tempId}`,
  );
}

export async function deleteContainerCatalogId(tempId) {
  return axios.delete(
    `${process.env.REACT_APP_TOOLING_API}/tooling/container/${tempId}`,
  );
}

export async function postContainerCatalog(formsData, tempId) {
  const params = {...formsData};

  if (tempId) {
  return axios.put(
    `${process.env.REACT_APP_TOOLING_API}/tooling/container/${tempId}`,
    {...params}
  );
  } else {
  return axios.post(
    `${process.env.REACT_APP_TOOLING_API}/tooling/container`,
    {...params}
  );
  }
}

// Configuration, Site level

export function getContainerConfiguration({
  pageNumber,
  pageSize,
  sortField,
  sortDirection,
  searchFilter,
  siteId
}) {
  return async () => {
    try {
      let params = {
        page: pageNumber,
        pageSize: pageSize,
      };
      if (sortField) {
        params.sort = sortField;
      }
      if (sortDirection) {
        params.sortDirection = sortDirection.toUpperCase(); //Available values : ASC, DESC
      }

      params = { ...params, ...searchFilter, site: siteId };

      const response = await axios.get(
        `${process.env.REACT_APP_TOOLING_API}/tooling/warehouse/container`,
        { params },
      );
      dispatch(slice.actions.getContainerConfigurationSuccess(response.data));
    } catch (error) {
      dispatch(
        displayToast("Failed to retrieve the data for Container Configuration.", SnackbarType.Negative)
      );
      dispatch(slice.actions.resetContainerConfiguration());
    }
  };
}

export async function getContainerConfigurationId(id) {
  return axios.get(
    `${process.env.REACT_APP_TOOLING_API}/tooling/warehouse/container/${id}`,
  );
}

export async function postContainerConfiguration(formsData, id) {
  const params = {...formsData};

  if (id) {
    return axios.put(
      `${process.env.REACT_APP_TOOLING_API}/tooling/warehouse/container/${id}`,
      {...params}
    );
  } else {
    return axios.post(
      `${process.env.REACT_APP_TOOLING_API}/tooling/warehouse/container`,
      {...params}
    );
  }
}

export async function postContainerEnabledStatus(id, operation) {
  if (operation === "enable") {
    return axios.post(
      `${process.env.REACT_APP_TOOLING_API}/tooling/warehouse/container/${id}/enable`
    );
  } else {
    return axios.post(
      `${process.env.REACT_APP_TOOLING_API}/tooling/warehouse/container/${id}/disable`
    );
  }
}

async function getTaskId(taskName) {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_TOOLING_API}/tooling/agenttask/find`,
      {
        params: {
          name: taskName,
        },
      },
    );
    return response.data?.id || null;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function postAgentContainerTaskStatus({
  site,
  user,
  environment,
  taskType,
  args = [],
  scheduledAt = new Date(),
  status = "New",
}) {
  try {
    const taskId = await getTaskId(taskType);

    const postBody = {
      site: site.id,
      user: {
        username: user.username,
        id: user.id,
      },
      taskType,
      task: taskId,
      arguments: args,
      scheduledAt: new Date(scheduledAt).toISOString(), // Ensure the date is in UTC format
      status,
    };

    const responseAgenttask = await axios.post(
      `${process.env.REACT_APP_TOOLING_API}/tooling/status/agenttask`,
      postBody, // Post body goes here
      {
        headers: {
          "locus-agent-env": environment,
        },
      },
    );
    return responseAgenttask;
  } catch (error) {
    console.error(error);
    throw error;
  };
}